/**
 * How a long scanner load is read: as consecutive waves of one capture (parity B-07).
 *
 * This is CapturePoint's main-process `scanner-waves.ts`, which Paige's browser stands in for. A
 * folder is split into captures on file edges, because each file is at least one document. A
 * scanner capture cannot be: every sheet side is its own file and the engine decides where
 * documents start. A 2,000-sheet duplex load was one reading of about 4,000 pages, nothing of it
 * in the job until all of it was read.
 *
 * So every wave but the last asks the engine to hold its trailing document (the engine's
 * WaveSeam): the documents before it are saved, the answer says how many files they took, and the
 * next wave starts at the held document's first file. No document is cut at a wave's edge and no
 * page is saved twice or skipped. Each wave is a run of its own, so its documents reach the job
 * and the review list as it ends, and another job takes its turn between waves.
 *
 * What differs from the desktop, on purpose: the cursor lives in this page, not in a manifest on a
 * disk, so the *last* request names every file of the capture under the capture's own identity
 * and the engine host reads only what the waves left (its CaptureWaves). That is the same request
 * Paige sends by itself when this tab goes away part-way, so a load is finished either way and a
 * retry after a failed wave joins that reading instead of starting a second one.
 */

/** Pages a wave. CapturePoint reads 160 where a card does the reading (48 on a processor alone). */
export const SCANNER_WAVE_PAGES = 160;

/** The engine identity of the wave that starts at `start`; the engine host writes the same words. */
export const scannerWaveAcquisitionId = (captureId: string, start: number): string => {
  if (!Number.isSafeInteger(start) || start < 0)
    throw new RangeError('A wave start must be a non-negative integer.');
  return `${captureId}~wave~${start}`;
};

/** Where a capture's waves have got to; kept on the staged capture for as long as this page lives. */
export interface ScannerWaveCursor {
  /** First file of the next wave. */
  cursor: number;
  /** How many files the next wave reads: more than a wave after one document ran past a whole wave. */
  span: number;
  /** The rest goes as one request (a wave failed, or the waves are done): the host knows where from. */
  whole?: boolean;
}

export interface ScannerWavePlan {
  start: number;
  /** One past the last file of this wave. */
  end: number;
  /** Reaches the capture's end: read by the last request, which holds nothing. */
  final: boolean;
}

/** The next wave from the cursor. */
export function nextScannerWave(totalFiles: number, at: ScannerWaveCursor): ScannerWavePlan {
  if (!Number.isSafeInteger(totalFiles) || totalFiles < 1)
    throw new RangeError('A capture must hold at least one file.');
  if (!Number.isSafeInteger(at.cursor) || at.cursor < 0 || at.cursor >= totalFiles)
    throw new RangeError("The wave position no longer matches the capture's pages.");
  if (!Number.isSafeInteger(at.span) || at.span < 1)
    throw new RangeError('The wave length is invalid.');
  const end = Math.min(totalFiles, at.cursor + at.span);
  return { start: at.cursor, end, final: end === totalFiles };
}

/**
 * Where the capture continues after a held wave, from the engine's own count. An impossible count
 * is refused rather than guessed: guessing "all" would drop the held document's pages, guessing
 * "none" would save the others twice. An absent count is an engine that holds nothing and saved
 * every file it was sent. Zero is one document longer than the whole wave: nothing was saved, and
 * a longer wave is read from the same start.
 */
export function advanceScannerWave(wave: ScannerWavePlan, consumed: unknown): ScannerWaveCursor {
  const length = wave.end - wave.start;
  if (consumed === undefined || consumed === null)
    return { cursor: wave.end, span: SCANNER_WAVE_PAGES };
  if (
    typeof consumed !== 'number' ||
    !Number.isSafeInteger(consumed) ||
    consumed < 0 ||
    consumed > length
  )
    throw new Error(
      'Cortexa did not say which pages of this part it saved. Every page is kept; Retry reads the rest.',
    );
  if (consumed === 0) return { cursor: wave.start, span: length + SCANNER_WAVE_PAGES };
  return { cursor: wave.start + consumed, span: SCANNER_WAVE_PAGES };
}

/** Whether a capture is read in waves: paper from a scanner, longer than one wave. */
export const readsInWaves = (sourceKind: string | undefined, fileCount: number): boolean =>
  sourceKind === 'scanner' && fileCount > SCANNER_WAVE_PAGES;

/** One request of the capture's reading: these files, under this identity, holding or not. */
export type WaveRun<T> = (wave: {
  files: string[];
  acquisitionId: string;
  holdTrailingDocument?: true;
}) => Promise<T>;

/**
 * Reads a scanner capture: its waves, each holding its trailing document, then the last request
 * for whatever they left. `state.waves` is advanced as each wave is saved, so a retry goes on from
 * there; a wave that fails sends the rest whole on the retry (see above).
 */
export async function readInWaves<T extends { consumedSourceCount?: number }>(
  captureId: string,
  files: string[],
  state: { waves?: ScannerWaveCursor },
  run: WaveRun<T>,
): Promise<T> {
  let at = state.waves ?? { cursor: 0, span: SCANNER_WAVE_PAGES };
  while (!at.whole) {
    const wave = nextScannerWave(files.length, at);
    if (wave.final) break;
    try {
      const answer = await run({
        files: files.slice(wave.start, wave.end),
        acquisitionId: scannerWaveAcquisitionId(captureId, wave.start),
        holdTrailingDocument: true,
      });
      at = advanceScannerWave(wave, answer.consumedSourceCount);
    } catch (reason) {
      state.waves = { ...at, whole: true };
      throw reason;
    }
    if (at.cursor >= files.length) at = { ...at, cursor: wave.start, whole: true };
    state.waves = at;
  }
  state.waves = { ...at, whole: true };
  return run({ files, acquisitionId: captureId });
}
